import { forwardRef, PropsWithChildren, useEffect, useRef } from 'react';

import { DataRoomClientFile } from '@/common/types';
import { NewFolderType } from '@/pages/overview/dataroom/content/common/types';
import { usePersistentScroll } from '@/pages/root/common/useScrollPosition';

interface ScrollableDivProps {
  containerStyle?: string;
  extraComponentStyling?: React.CSSProperties;
  selectedFile?: DataRoomClientFile;
  newlyCreatedFolder?: { folderPath: string; isFolderCreated: boolean };
  setNewlyCreatedFolder?: (val: NewFolderType) => void;
  scrollKey?: string;
  onScroll?: () => void;
}

export const ScrollableDiv = forwardRef<HTMLDivElement, PropsWithChildren<ScrollableDivProps>>(
  (
    {
      children,
      containerStyle,
      extraComponentStyling,
      selectedFile,
      newlyCreatedFolder,
      setNewlyCreatedFolder,
      onScroll,
      scrollKey,
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const divRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;
    const hasRestoredScroll = useRef(false);
    usePersistentScroll(divRef, scrollKey || 'default');

    // Save scroll position to sessionStorage
    const saveScrollPosition = () => {
      if (!divRef.current) return;

      const scrollTop = divRef.current.scrollTop;
      const scrollLeft = divRef.current.scrollLeft;
      const storedScrollKey = `scrollable-div-${scrollKey || 'default'}`;
      sessionStorage.setItem(storedScrollKey, JSON.stringify({ top: scrollTop, left: scrollLeft }));
    };

    // Handle scroll events
    const handleScroll = () => {
      saveScrollPosition();
      onScroll?.();
    };

    // Reset scroll restoration flag when component remounts
    useEffect(() => {
      hasRestoredScroll.current = false;
    }, []);

    //This useEffect contols the scroll functionality in the data room file list
    useEffect(() => {
      if (
        newlyCreatedFolder &&
        setNewlyCreatedFolder &&
        newlyCreatedFolder.isFolderCreated === true
      ) {
        setTimeout(() => {
          const selectedElement = document.getElementById(newlyCreatedFolder.folderPath);
          const container = document.getElementById('scrollable-div');
          if (selectedElement && container) {
            selectedElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          setTimeout(() => {
            setNewlyCreatedFolder({ folderPath: '', isFolderCreated: false });
          }, 2000);
        }, 500);
      }
    }, [
      newlyCreatedFolder,
      newlyCreatedFolder?.folderPath,
      newlyCreatedFolder?.isFolderCreated,
      selectedFile,
      setNewlyCreatedFolder,
    ]);

    return (
      <div
        className={`${containerStyle} scroll-p-10 overflow-y-auto scrollbar scrollbar-thumb-[#292829] scrollbar-thumb-rounded scrollbar-w-[10px] active:scrollbar-thumb-[#302e30]`}
        data-testid="scrollable-div"
        id="scrollable-div"
        ref={divRef}
        onScroll={handleScroll}
        style={extraComponentStyling ? extraComponentStyling : {}}
      >
        {children}
      </div>
    );
  },
);

ScrollableDiv.displayName = 'ScrollableDiv';
